import { isEqual, omit } from 'lodash-es';

import type {
  DocumentSectionSpec,
  DocumentTemplate,
} from '@/entities/templates';

import { EMPTY_BLOCK_PRESET, type NoteBlockPreset } from '../config';

import {
  addPresetOwnOption,
  EMPTY_LINE_PRESETS,
  hasPresetValue,
  orderPresetSectionLines,
  type BlockPresets,
  type NotePresets,
  type PresetAddedLine,
} from './blockPresets';
import {
  createNoteBlock,
  createNoteLine,
  setNoteChoiceValues,
} from './blockTemplate';
import {
  findNamedNoteValue,
  isNoteBlockMultiple,
  type NoteBlockDef,
  type NoteLineDef,
} from './noteView';

const readBlockDefault = (block: NoteBlockDef): NoteBlockPreset | null => {
  const value = block.Default;
  if (!value) {
    return null;
  }
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice':
    case 'ListRef': {
      const chosen = { ...EMPTY_BLOCK_PRESET, values: value.Values };
      return value.Text
        ? addPresetOwnOption(chosen, value.Text, isNoteBlockMultiple(block))
        : chosen;
    }
    case 'Teeth':
      return {
        ...EMPTY_BLOCK_PRESET,
        teeth: value.Teeth,
        teethGroups: value.TeethGroups,
        ...(value.Marks.length > 0 && { marks: value.Marks }),
        ...(value.None && { none: true }),
      };
    case 'FreeText':
      return { ...EMPTY_BLOCK_PRESET, text: value.Text };
    case 'Date':
      return value.Date ? { ...EMPTY_BLOCK_PRESET, date: value.Date } : null;
    case 'Number':
      return value.Numbers.length
        ? { ...EMPTY_BLOCK_PRESET, numbers: value.Numbers }
        : null;
    case 'Table':
      return value.TableRows.length
        ? { ...EMPTY_BLOCK_PRESET, tableRows: value.TableRows }
        : null;
    default:
      return null;
  }
};

// The values a note of the template version starts with before the start:
// its blocks' defaults, in the shape the note's controls set them.
export const getTemplateDefaultPresets = (
  template: DocumentTemplate,
): BlockPresets =>
  Object.fromEntries(
    template.sections.flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.flatMap((block) => {
          const preset = readBlockDefault(block);
          return preset && hasPresetValue(preset)
            ? [[block.ID, preset] as const]
            : [];
        }),
      ),
    ),
  );

const setTextWording = (block: NoteBlockDef, Value: string): NoteBlockDef =>
  block.Kind.case === 'Text'
    ? {
        ...block,
        Kind: { case: 'Text', value: { ...block.Kind.value, Value } },
      }
    : block;

const listChosenValues = ({ values, text }: NoteBlockPreset) => [
  ...values,
  ...(text.trim() ? [text.trim()] : []),
];

// What the note set up before the start changes in a block of the template:
// the wording typed over a text block, and the own values of a choice, which
// become its options in the order they were added. A clinic list holds its
// own options, so an own value there stays this recording's value.
const applyBlockPreset = (
  block: NoteBlockDef,
  preset: NoteBlockPreset | undefined,
): NoteBlockDef => {
  if (block.Kind.case === 'Text') {
    return preset?.text.trim() ? setTextWording(block, preset.text) : block;
  }
  if (block.Kind.case !== 'Choice' && block.Kind.case !== 'MultiChoice') {
    return block;
  }
  const offered = block.Kind.value.Values;
  const options = (preset?.ownOptions ?? []).reduce<string[]>(
    (values, value) =>
      findNamedNoteValue(values, value) ? values : [...values, value],
    offered,
  );
  return options === offered ? block : setNoteChoiceValues(block, options);
};

// A line whose every link reads a block of a removed line can no longer
// show, as on the note, so it goes as well.
const listRemovedLineIds = (
  lines: NoteLineDef[],
  removed: string[],
): string[] => {
  const removedBlockIds = new Set(
    lines
      .filter((line) => removed.includes(line.ID))
      .flatMap((line) => line.Blocks.map((block) => block.ID)),
  );
  const unreachable = lines.filter(
    (line) =>
      !removed.includes(line.ID) &&
      line.ShowWhen.length > 0 &&
      line.ShowWhen.every(({ BlockID }) => removedBlockIds.has(BlockID)),
  );
  return unreachable.length
    ? listRemovedLineIds(lines, [
        ...removed,
        ...unreachable.map((line) => line.ID),
      ])
    : removed;
};

const toTemplateLine = ({ text }: PresetAddedLine): NoteLineDef =>
  createNoteLine(setTextWording(createNoteBlock('text'), text));

// "Save as my template" from the note set up before the start: its wording,
// added and removed lines and line order become the template's lines, and
// the own values become options of their blocks in the order they were
// added. The values chosen stay this recording's: a value the template
// recommends is set in the template editor only. Ids
// and slot ids stay; added lines take temporary ids.
export const buildPresetTemplate = (
  template: DocumentTemplate,
  { blocks, lines }: NotePresets,
): DocumentTemplate => {
  const templateLines = template.sections.flatMap((section) => section.Lines);
  const removed = new Set(listRemovedLineIds(templateLines, lines.deleted));
  const removedBlockIds = new Set(
    templateLines
      .filter((line) => removed.has(line.ID))
      .flatMap((line) => line.Blocks.map((block) => block.ID)),
  );
  const applyLine = (line: NoteLineDef): NoteLineDef => ({
    ...line,
    ShowWhen: line.ShowWhen.filter(
      ({ BlockID }) => !removedBlockIds.has(BlockID),
    ),
    Blocks: line.Blocks.map((block) =>
      applyBlockPreset(block, blocks[block.ID]),
    ),
  });
  const applySection = (section: DocumentSectionSpec): DocumentSectionSpec => ({
    ...section,
    Lines: orderPresetSectionLines(section, lines)
      .filter((lineId) => !removed.has(lineId))
      .flatMap((lineId) => {
        const line = section.Lines.find((item) => item.ID === lineId);
        const added = lines.added.find((item) => item.id === lineId);
        if (line) {
          return [applyLine(line)];
        }
        return added ? [toTemplateLine(added)] : [];
      }),
  });
  return { ...template, sections: template.sections.map(applySection) };
};

// Whether the note set up before the start changed the template itself, as
// "Save as my template" would save it: a value chosen is no change of it.
export const holdsPresetTemplateChanges = ({
  source,
  template,
  presets,
}: {
  source: DocumentTemplate;
  template: DocumentTemplate;
  presets: NotePresets;
}) => !isEqual(buildPresetTemplate(template, presets), source);

// The values chosen before the start, kept on the version "Save as my
// template" saved: an own value it made an option is chosen as that option,
// and the wording and the lines are the version's own now.
export const keepPresetValues = (
  saved: DocumentTemplate,
  { blocks }: NotePresets,
): NotePresets => {
  const savedBlocks = new Map(
    saved.sections.flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.map((block): [string, NoteBlockDef] => [block.ID, block]),
      ),
    ),
  );
  return {
    blocks: Object.entries(blocks).reduce<BlockPresets>(
      (kept, [blockId, preset]) => {
        const block = savedBlocks.get(blockId);
        if (!block || block.Kind.case === 'Text') {
          return kept;
        }
        if (block.Kind.case !== 'Choice' && block.Kind.case !== 'MultiChoice') {
          return { ...kept, [blockId]: preset };
        }
        const options = block.Kind.value.Values;
        const values = listChosenValues(preset).map(
          (value) => findNamedNoteValue(options, value) ?? value,
        );
        return {
          ...kept,
          [blockId]: {
            ...omit(preset, 'ownOptions'),
            values: [...new Set(values)],
            text: '',
          },
        };
      },
      {},
    ),
    lines: EMPTY_LINE_PRESETS,
  };
};
